import axios from "axios";
import { API_CONFIG } from "../config";

interface Business {
  id: string;
  name: string;
}

interface Account {
  id: string;
  businessId: string;
  identifier: string;
  isActive: boolean;
  mid: string;
  gmpw: string;
  paymentGateway: string;
  allowedMaxAmount: number;
  terminals: Terminal[];
  // Add other account properties as needed
}

interface Terminal {
  id: string;
  identifier: string;
  tid: string;
  serialNumber: string;
  isActive: boolean;
  lastUpdatedBy: string;
  lastUpdatedDate: Date;
  // Add other terminal properties as needed
}

class AccountApiClient {
  /**
   * Fetch businesses for the authenticated user
   */
  static async fetchBusinesses(): Promise<Business[]> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return [];
      }

      const response = await axios.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BUSINESSES}`,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Businesses response:", response.data);

      // More robust data handling
      const businessData = Array.isArray(response.data)
        ? response.data
        : response.data?.data
        ? response.data.data
        : [];

      return businessData.map((business: any) => ({
        id: business?.id,
        name: business?.name,
      }));
    } catch (error: any) {
      console.error("Error fetching businesses:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      return [];
    }
  }

  /**
   * Fetch accounts for a specific business
   */
  static async fetchAccounts(businessId: string): Promise<Account[]> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return [];
      }

      if (!businessId) {
        console.log("No business ID provided");
        return [];
      }

      const response = await axios.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCOUNTS(businessId)}`,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Accounts response:", response.data);

      // More robust data handling
      const accountData = Array.isArray(response.data)
        ? response.data
        : response.data?.data
        ? response.data.data
        : [];

      return accountData.map((account: any) => ({
        id: account?.id,
        businessId: account?.businessId,
        identifier: account?.identifier,
        isActive: account?.isActive,
        mid: account?.mid,
        gmpw: account?.gmpw,
        paymentGateway: account?.paymentGateway,
        allowedMaxAmount: account?.allowedMaxAmount || 0,
        terminals: Array.isArray(account?.terminals)
          ? account.terminals.map((terminal: any) => ({
              id: terminal?.id,
              identifier: terminal?.identifier,
              tid: terminal?.tid,
              serialNumber: terminal?.serialNumber,
              isActive: terminal?.isActive,
              lastUpdatedBy: terminal?.lastUpdatedBy,
              lastUpdatedDate: terminal?.lastUpdatedDate
                ? new Date(terminal.lastUpdatedDate)
                : new Date(),
            }))
          : [],
      }));
    } catch (error: any) {
      console.error("Error fetching accounts:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      return [];
    }
  }

  /**
   * Add a new business
   */
  static async addBusiness(businessName: string): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        throw new Error("No authentication token found");
      }

      if (!businessName) {
        throw new Error("Business name is required");
      }

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/add-business`,
        { name: businessName },
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      console.log("Add business response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error adding business:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Add a new account to a business
   */
  static async addAccount(
    businessId: string,
    accountData: {
      identifier: string;
      mid: string;
      gmpw: string;
      paymentGateway: string;
      allowedMaxAmount: number;
    }
  ): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        throw new Error("No authentication token found");
      }

      if (!businessId) {
        throw new Error("Business ID is required");
      }

      if (!accountData.identifier || !accountData.mid || !accountData.gmpw || !accountData.paymentGateway) {
        throw new Error("Account identifier, MID, GMPW, and payment gateway are required");
      }

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/payment/v2/account/${businessId}`,
        accountData,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      console.log("Add account response:", response.data);
      return response;
    } catch (error: any) {
      console.error("Error adding account:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Update an existing account
   */
  static async updateAccount(
    businessId: string,
    accountId: string,
    accountData: {
      mid: string;
      gmpw: string;
      paymentGateway: string;
      allowedMaxAmount: number;
      isActive: boolean;
    }
  ): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        throw new Error("No authentication token found");
      }

      if (!businessId || !accountId) {
        throw new Error("Business ID and Account ID are required");
      }

      if (!accountData.mid || !accountData.gmpw || !accountData.paymentGateway) {
        throw new Error("MID, GMPW, and payment gateway are required");
      }

      const response = await axios.put(
        `${API_CONFIG.BASE_URL}/api/payment/v2/account/${businessId}/update-account/${accountId}`,
        accountData,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      console.log("Update account response:", response.data);
      return response;
    } catch (error: any) {
      console.error("Error updating account:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Add a new terminal to an account
   */
  static async addTerminal(
    businessId: string,
    accountId: string,
    terminalData: {
      identifier: string;
      tid: string;
      serialNumber: string;
      isActive: boolean;
    }
  ): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        throw new Error("No authentication token found");
      }

      if (!businessId || !accountId) {
        throw new Error("Business ID and Account ID are required");
      }

      if (!terminalData.identifier || !terminalData.tid || !terminalData.serialNumber) {
        throw new Error("Terminal identifier, TID, and serial number are required");
      }

      // Always set isActive to true for new terminals
      const payload = {
        ...terminalData,
        isActive: true
      };

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/payment/v2/account/${businessId}/${accountId}/add-terminal`,
        payload,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      console.log("Add terminal response:", response.data);
      return response;
    } catch (error: any) {
      console.error("Error adding terminal:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Update an existing terminal
   */
  static async updateTerminal(
    businessId: string,
    accountId: string,
    terminalId: string,
    terminalData: {
      tid: string;
      serialNumber: string;
      isActive: boolean;
    }
  ): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        throw new Error("No authentication token found");
      }

      if (!businessId || !accountId || !terminalId) {
        throw new Error("Business ID, Account ID, and Terminal ID are required");
      }

      if (!terminalData.tid || !terminalData.serialNumber) {
        throw new Error("TID and serial number are required");
      }

      const response = await axios.put(
        `${API_CONFIG.BASE_URL}/api/payment/v2/account/${businessId}/${accountId}/update-terminal/${terminalId}`,
        terminalData,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      console.log("Update terminal response:", response.data);
      return response;
    } catch (error: any) {
      console.error("Error updating terminal:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }
}

export default AccountApiClient;
